import React, {useState, useEffect} from 'react';
import SideBarItems from "../constants/SideBarItems";
import {Button, Layout, Menu} from 'antd';
import {ClipboardList, LogOut, Menu as MenuIcon, MessageCircleMore, UserRoundCog, X} from 'lucide-react';
import Header from '../components/Header/Header';
import {Link, useLocation, Outlet, useNavigate} from 'react-router-dom';
import {useTranslation} from "react-i18next";
import {useDispatch} from 'react-redux';
import SocialButtons from "../components/SocialButtons/SocialButtons";
import {logout} from "../store/auth/user.action";
import {getStoredUser, USER_STORAGE_UPDATED_EVENT} from "../utils";
import {images} from "../constants";
import UserRoleMask from "../components/UserRoleMask/UserRoleMask";
import "./SideBar.css";

const {Content, Footer, Sider} = Layout;
const SIDEBAR_WIDTH = 200;
const SIDEBAR_COLLAPSED_WIDTH = 80;
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';
const MOBILE_QUERY = '(max-width: 768px)';

const isMobileViewport = () => window.matchMedia(MOBILE_QUERY).matches;

// Desktop collapse preference survives reloads; mobile always starts closed
const readStoredCollapsed = () => {
    try {
        return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch (err) {
        return false;
    }
};

const writeStoredCollapsed = (value) => {
    try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
    } catch (err) {
        // storage unavailable (private mode); keep the in-memory state only
    }
};

const SideBar = () => {
    const {t} = useTranslation();
    const items = SideBarItems(t);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // Read the viewport on first render so phones don't animate from the desktop layout
    const [collapsed, setCollapsed] = useState(() => isMobileViewport() || readStoredCollapsed())
    const [isMobile, setIsMobile] = useState(isMobileViewport)
    const [isScrolled, setIsScrolled] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [userData, setUserData] = useState(() => getStoredUser() || {});

    let {pathname} = useLocation();
    pathname = pathname.replace('/', '')

    useEffect(() => {
        const mediaQuery = window.matchMedia(MOBILE_QUERY);

        const handleViewportChange = (event) => {
            setIsMobile(event.matches);
            setCollapsed(event.matches ? true : readStoredCollapsed());
        };

        handleViewportChange(mediaQuery);
        mediaQuery.addEventListener('change', handleViewportChange);

        return () => {
            mediaQuery.removeEventListener('change', handleViewportChange);
        };
    }, []);

    useEffect(() => {
        if (isMobile) {
            setCollapsed(true);
        }
    }, [isMobile, pathname]);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 4);

        handleScroll();
        window.addEventListener('scroll', handleScroll, {passive: true});

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const refreshUserData = () => {
            const items = getStoredUser();

            if (items) {
                setUserData(items);
            }
        };

        refreshUserData();
        window.addEventListener(USER_STORAGE_UPDATED_EVENT, refreshUserData);
        window.addEventListener('storage', refreshUserData);

        return () => {
            window.removeEventListener(USER_STORAGE_UPDATED_EVENT, refreshUserData);
            window.removeEventListener('storage', refreshUserData);
        };
    }, []);

    const handleCollapse = (value) => {
        setCollapsed(value);

        if (!isMobile) {
            writeStoredCollapsed(value);
        }
    };

    const handleMenuClick = () => {
        if (isMobile) {
            setCollapsed(true);
        }
    };

    const handleMobileProfileClick = () => {
        if (isMobile) {
            setCollapsed(true);
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate("/", {replace: true});
        handleMobileProfileClick();
    };

    return (
        <section className={`sidebar${isMobile && !collapsed ? ' sidebar--mobile-open' : ''}`}>
            <Button
                type="default"
                shape="circle"
                className="mobile-sidebar-toggle"
                aria-label={collapsed ? 'Open navigation' : 'Close navigation'}
                icon={collapsed ? <MenuIcon size={20}/> : <X size={20}/>}
                onClick={() => setCollapsed((current) => !current)}
            />
            {isMobile && !collapsed && (
                <button
                    type="button"
                    className="mobile-sidebar-backdrop"
                    aria-label="Close navigation"
                    onClick={() => setCollapsed(true)}
                />
            )}
            <Link
                to="/analysis"
                className={`sidebar-logo${collapsed && !isMobile ? ' sidebar-logo--collapsed' : ''}${isScrolled ? ' sidebar-logo--scrolled' : ''}`}
                style={{width: isMobile ? undefined : collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}}
            >
                <img src={collapsed && !isMobile ? images.logoFboxNoFrame : images.newLogo} alt="logo"/>
            </Link>
            <Layout style={{minHeight: '100vh'}}>
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={handleCollapse}
                    theme="light"
                    width={SIDEBAR_WIDTH}
                    collapsedWidth={isMobile ? 0 : SIDEBAR_COLLAPSED_WIDTH}
                    className="liquid-glass-sidebar"
                >
                    <div className="mobile-sidebar-profile">
                        <div className="mobile-sidebar-profile__meta">
                            <span className="mobile-sidebar-profile__name">
                                {userData?.data?.username}
                            </span>
                            <span className="mobile-sidebar-profile__role">
                                {userData?.data?.is_admin ? 'Admin' : 'Client'}
                            </span>
                        </div>
                        {isMobile && userData?.data?.is_admin && (
                            <div className="mobile-sidebar-profile__mask">
                                <UserRoleMask
                                    userData={userData}
                                    placeholder={t("pages.orders.listColumns.maskUser")}
                                />
                            </div>
                        )}
                        <Link to="/profile" onClick={handleMobileProfileClick}>
                            <UserRoundCog size={16}/>
                            <span>{t('header.userMenuBar.column1')}</span>
                        </Link>
                        <Link to="/feedback" onClick={handleMobileProfileClick}>
                            <MessageCircleMore size={16}/>
                            <span>{t('header.userMenuBar.column2')}</span>
                        </Link>
                        {userData?.data?.is_admin && (
                            <Link to="/audit-logs" onClick={handleMobileProfileClick}>
                                <ClipboardList size={16}/>
                                <span>{t('header.userMenuBar.column4')}</span>
                            </Link>
                        )}
                        <button type="button" onClick={handleLogout}>
                            <LogOut size={16}/>
                            <span>{t('header.userMenuBar.column3')}</span>
                        </button>
                    </div>

                    <Menu
                        mode="inline"
                        items={items}
                        className="liquid-glass-menu"
                        onClick={handleMenuClick}
                    />

                    <div className="fixed-social-buttons">
                        <SocialButtons vertical={collapsed && !isMobile}/>
                    </div>
                </Sider>

                <Layout
                    className="sidebar__main"
                    style={{
                        marginLeft: isMobile
                            ? 0
                            : collapsed
                            ? SIDEBAR_COLLAPSED_WIDTH
                            : SIDEBAR_WIDTH,
                        transition: 'margin-left 0.2s',
                    }}
                >
                    <Header
                        isCollapse={collapsed}
                        isScrolled={isScrolled}
                        searchText={searchText}
                        setSearchText={setSearchText}
                    />

                    <Content style={{margin: isMobile ? 0 : '0 16px'}}>
                        {/*<Breadcrumb*/}
                        {/*    style={{margin: '16px 0', textTransform: 'capitalize'}}*/}
                        {/*    items={[*/}
                        {/*        {title: <NavLink to="/analysis">Main</NavLink>},*/}
                        {/*        {*/}
                        {/*            title: pathname.replace('/', ''),*/}
                        {/*            className: 'breadcrumb-capitalize',*/}
                        {/*        },*/}
                        {/*    ]}*/}
                        {/*/>*/}
                        <Outlet context={{searchText, setSearchText}}/>
                    </Content>

                    <Footer style={{textAlign: 'center'}}>
                        BCTechnologies ©{new Date().getFullYear()}
                    </Footer>
                </Layout>
            </Layout>
        </section>
    );
}

export default SideBar;
